import apiService from '../../../services/api'

/**
 * Прямой fetch-запрос к API профиля.
 * Используется вместо apiService.makeRequest, чтобы читать тело ошибок
 * (403 like_not_allowed, 404 not_found, 400 invalid_color_*) — makeRequest
 * на 403 бросает generic-ошибку без парсинга тела ответа.
 * @returns {Promise<{ok: boolean, status: number, data: any}>}
 */
async function request(path, { method = 'GET', body } = {}) {
  let response, authContext
  try {
    ({ response, authContext } = await apiService.fetchWithAuth(path, {
      method,
      body: body !== undefined ? JSON.stringify(body) : undefined
    }))
  } catch (error) {
    console.error('Ошибка запроса профиля:', error)
    return { ok: false, status: 0, data: null }
  }

  let data = null
  try {
    data = await response.json()
  } catch (e) {
    // тело может быть пустым
  }

  if (apiService.getAuthContext().token !== authContext.token) return { ok: false, status: 0, data: null }
  return { ok: response.ok, status: response.status, data }
}

/**
 * Multipart-запрос (загрузка файла фона). Content-Type НЕ выставляем —
 * браузер сам проставит его с boundary для FormData.
 * @returns {Promise<{ok: boolean, status: number, data: any}>}
 */
async function multipartRequest(path, formData) {
  let response
  try {
    ({ response } = await apiService.fetchWithAuth(path, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData
    }))
  } catch (error) {
    console.error('Ошибка запроса профиля:', error)
    return { ok: false, status: 0, data: null }
  }

  // 413 от nginx приходит HTML-страницей без JSON — тело не разбираем
  let data = null
  if (response.status !== 413) {
    try {
      data = await response.json()
    } catch (e) {
      // тело может быть пустым
    }
  }

  return { ok: response.ok, status: response.status, data }
}

export const profileApi = {
  /** Свой профиль (GET /api/profile/) */
  getMyProfile: () => request('/api/profile/'),

  /** Профиль по slug (GET /api/profile/{slug}/) */
  getProfile: (slug) => request(`/api/profile/${slug}/`),

  getMyAchievements: () => request('/api/profile/achievements/'),

  getProfileAchievements: (slug, language) => {
    const authContext = apiService.getAuthContext()
    const query = !authContext.isAuthorized && language
      ? `?lang=${encodeURIComponent(language)}`
      : ''
    return request(`/api/profile/${slug}/achievements/${query}`)
  },

  setAchievementShowcase: (achievements) => request('/api/profile/achievements/showcase/', {
    method: 'POST',
    body: { achievements }
  }),

  /** Смена уровня доступа: hidden / partial / public (POST /api/profile/visibility/) */
  setVisibility: (visibility) => request('/api/profile/visibility/', { method: 'POST', body: { visibility } }),

  /**
   * Частичное сохранение цветов (POST /api/profile/colors/):
   * переданный ключ сохраняется, null сбрасывает к стандартному, непереданный не меняется.
   * Ответ 200 — свой профиль целиком (как GET /api/profile/).
   */
  saveColors: (colors) => request('/api/profile/colors/', { method: 'POST', body: colors }),

  /** Лайк: желаемое состояние 1/0, не «переключи» (POST /api/profile/{slug}/like/) */
  setLike: (slug, like) => request(`/api/profile/${slug}/like/`, { method: 'POST', body: { like } }),

  /**
   * Переключатель «Показывать мой Telegram»: желаемое состояние 1/0 (POST /api/profile/contacts/).
   * Ответ 200 — свой профиль целиком (show_telegram и contacts в нём уже актуальные).
   */
  setContacts: (telegram) => request('/api/profile/contacts/', { method: 'POST', body: { telegram } }),

  /**
   * Загрузка картинки-фона карточки профиля (POST /api/profile/background/, multipart).
   * settings — необязательные fit/position_x/position_y/overlay/blur для этой картинки.
   * Ответ 200 — свой профиль целиком (background уже в нём).
   */
  uploadBackground: (file, settings) => {
    const formData = new FormData()
    formData.append('file', file.blob, file.name)
    if (settings) {
      Object.keys(settings).forEach((key) => {
        formData.append(key, settings[key])
      })
    }
    return multipartRequest('/api/profile/background/', formData)
  },

  /**
   * Частичное сохранение настроек фона без перезагрузки картинки
   * (POST /api/profile/background/settings/): переданный ключ сохраняется,
   * отсутствующий не меняется. Ответ 200 — свой профиль целиком.
   */
  saveBackgroundSettings: (settings) => request('/api/profile/background/settings/', {
    method: 'POST',
    body: settings
  }),

  /** Снятие фона (POST /api/profile/background/delete/). Ответ 200 — свой профиль с background: null */
  deleteBackground: () => request('/api/profile/background/delete/', { method: 'POST' }),

  uploadAvatar: (file) => {
    const formData = new FormData()
    formData.append('file', file.blob, file.name)
    return multipartRequest('/api/profile/avatar/', formData)
  },

  deleteAvatar: () => request('/api/profile/avatar/delete/', { method: 'POST' }),

  /** Prepared message «Поделиться» для Telegram.WebApp.shareMessage (POST /api/profile/share/) */
  share: () => request('/api/profile/share/', { method: 'POST', body: {} })
}
